// src/hooks/useSupabaseTrades.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export function useSupabaseTrades(accountId) {
  const { user } = useAuth();
  const [trades,  setTrades]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const addingRef = useRef(false);

  const fetchTrades = useCallback(async () => {
    if (!user) { setTrades([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      let query = supabase
        .from('trades')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .order('created_at', { ascending: false });
      if (accountId) query = query.eq('account_id', accountId);
      const { data, error } = await query;
      if (error) throw error;
      setTrades((data || []).map(mapTrade));
    } catch (e) {
      setError(e.message || 'Failed to load trades.');
      setTrades([]);
    } finally {
      setLoading(false);
    }
  }, [user, accountId]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const addTrade = async (trade) => {
    if (!user || addingRef.current) return { error: { message: 'Already saving.' } };
    addingRef.current = true;
    try {
      const { data, error } = await supabase
        .from('trades')
        .insert([{
          user_id:         user.id,
          account_id:      trade.accountId || accountId || null,
          date:            trade.date,
          pair:            trade.pair,
          session:         trade.session,
          direction:       trade.direction,
          entry_price:     Number(trade.entryPrice)   || null,
          stop_loss:       Number(trade.stopLoss)     || null,
          take_profit:     Number(trade.takeProfit)   || null,
          risk_amount:     Number(trade.riskAmount)   || null,
          lot_size:        Number(trade.lotSize)      || null,
          contracts:       Number(trade.contracts)    || null,
          pnl:             Number(trade.pnl)          || 0,
          result:          trade.result,
          rr_achieved:     Number(trade.rrAchieved)   || null,
          screenshot_link: trade.screenshotLink       || null,
          notes:           trade.notes               || null,
          mistake_made:    trade.mistakeMade          || null,
          followed_rules:  trade.followedRules        ?? true,
          emotion_before:  trade.emotionBefore        || null,
          emotion_after:   trade.emotionAfter         || null,
          poi:             trade.poi                  || null,
          order_flow:      trade.orderFlow            || null,
          analysis_tf:     trade.analysisTF           || null,
          status:          trade.status               || 'closed',
          source:          trade.source               || 'manual',
          external_id:     trade.externalId           || null,
        }])
        .select()
        .single();
      if (!error && data) setTrades(prev => [mapTrade(data), ...prev]);
      return { error };
    } catch (e) {
      return { error: { message: 'Network error. Trade not saved.' } };
    } finally {
      addingRef.current = false;
    }
  };

  const updateTrade = async (id, updates) => {
    if (!user || !id) return;
    // Map camelCase to snake_case
    const dbUpdates = {};
    if (updates.pnl           !== undefined) dbUpdates.pnl             = Number(updates.pnl) || 0;
    if (updates.result        !== undefined) dbUpdates.result           = updates.result;
    if (updates.notes         !== undefined) dbUpdates.notes            = updates.notes;
    if (updates.mistakeMade   !== undefined) dbUpdates.mistake_made     = updates.mistakeMade;
    if (updates.rrAchieved    !== undefined) dbUpdates.rr_achieved      = Number(updates.rrAchieved) || null;
    if (updates.stopLoss      !== undefined) dbUpdates.stop_loss        = Number(updates.stopLoss) || null;
    if (updates.takeProfit    !== undefined) dbUpdates.take_profit      = Number(updates.takeProfit) || null;
    if (updates.screenshotLink!== undefined) dbUpdates.screenshot_link  = updates.screenshotLink;
    if (updates.status        !== undefined) dbUpdates.status           = updates.status;
    if (updates.entryPrice    !== undefined) dbUpdates.entry_price      = Number(updates.entryPrice) || null;

    const { data, error } = await supabase
      .from('trades')
      .update(dbUpdates)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (!error && data) {
      setTrades(prev => prev.map(t => t.id === id ? mapTrade(data) : t));
    }
    return { error };
  };

  const deleteTrade = async (id) => {
    if (!user || !id) return;
    setTrades(prev => prev.filter(t => t.id !== id));
    const { error } = await supabase.from('trades').delete().eq('id', id).eq('user_id', user.id);
    if (error) fetchTrades(); // revert on failure
    return { error };
  };

  const closeTrade = async (id, closeData) => {
    return updateTrade(id, {
      status:     'closed',
      pnl:        closeData.pnl,
      result:     closeData.result,
      rrAchieved: closeData.rrAchieved,
      takeProfit: closeData.exitPrice,
      notes:      closeData.notes,
    });
  };

  return { trades, loading, error, addTrade, updateTrade, deleteTrade, closeTrade, refetch: fetchTrades };
}

function mapTrade(t) {
  return {
    id:             t.id,
    date:           t.date,
    pair:           t.pair,
    session:        t.session,
    direction:      t.direction,
    entryPrice:     t.entry_price,
    stopLoss:       t.stop_loss,
    takeProfit:     t.take_profit,
    riskAmount:     t.risk_amount,
    lotSize:        t.lot_size,
    contracts:      t.contracts || 0,
    pnl:            Number(t.pnl) || 0,
    result:         t.result,
    rrAchieved:     t.rr_achieved,
    screenshotLink: t.screenshot_link,
    notes:          t.notes,
    mistakeMade:    t.mistake_made,
    followedRules:  t.followed_rules,
    emotionBefore:  t.emotion_before,
    emotionAfter:   t.emotion_after,
    poi:            t.poi,
    orderFlow:      t.order_flow,
    analysisTF:     t.analysis_tf,
    status:         t.status || 'closed',
    source:         t.source || 'manual',
    externalId:     t.external_id,
    accountId:      t.account_id,
    createdAt:      t.created_at,
  };
}
